import { useQuery, useMutation } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { formatEUR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Package, FileCheck2, TrendingUp, Clock, CheckCircle2, BellRing } from "lucide-react";
import { Link } from "react-router-dom";
import { Leaderboard } from "@/components/Leaderboard";

const Kpi = ({ icon: Icon, label, value, hint, testid, accent = "#B84A2B" }) => (
  <div data-testid={testid} className="bg-white border border-[#E2DDD5] rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}1A`, color: accent }}>
        <Icon size={14} />
      </div>
    </div>
    <div className="mt-3 text-2xl font-bold tracking-tight">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

const RemindersCard = ({ api }) => {
  const { data: last, refetch } = useQuery({
    queryKey: ["admin-last-reminder"],
    queryFn: async () => (await api.get("/admin/renewal-reminders/last")).data,
    refetchInterval: (q) => (q.state.data?.started_at && !q.state.data?.finished_at ? 3000 : false),
  });
  const send = useMutation({
    mutationFn: async () => (await api.post("/admin/renewal-reminders/send-now")).data,
    onSuccess: () => { toast.success("Invio avvisi avviato"); setTimeout(() => refetch(), 1500); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  return (
    <div className="bg-white border border-[#E2DDD5] rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="reminders-card">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#C69214]/15 text-[#C69214] flex items-center justify-center"><BellRing size={16} /></div>
        <div>
          <div className="font-semibold">Avvisi rinnovo clienti</div>
          <div className="text-sm text-slate-500">Ogni lunedì alle 9:00 i rivenditori ricevono i clienti in scadenza entro 30 giorni; Lei riceve il riepilogo.</div>
          <div className="text-xs text-slate-400 mt-1" data-testid="reminders-last">
            {last?.started_at ? (last.finished_at
              ? (last.status === "failed" ? `Ultimo invio non completato: ${new Date(last.started_at).toLocaleString("it-IT")}` : `Ultimo invio: ${new Date(last.started_at).toLocaleString("it-IT")} · ${last.customers ?? 0} ${last.customers === 1 ? "cliente" : "clienti"} · ${last.emails_sent ?? 0} email ai rivenditori`)
              : `Invio in corso (avviato ${new Date(last.started_at).toLocaleString("it-IT")})`) : "Nessun invio ancora effettuato"}
          </div>
        </div>
      </div>
      <Button variant="outline" disabled={send.isPending} onClick={() => send.mutate()} data-testid="btn-send-reminders">Invia ora</Button>
    </div>
  );
};

export default function AdminDashboard() {
  const api = useApi();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: async () => (await api.get("/admin/dashboard")).data,
  });

  if (isLoading) return <div className="text-slate-500">Caricamento…</div>;
  const d = data || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard Super Admin</h1>
        <p className="text-sm text-slate-500 mt-1">Panoramica rivenditori, prodotti e provvigioni GoWeb.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Rivenditori" value={d.resellers ?? 0} hint={`${d.pending_resellers ?? 0} in attesa`} testid="kpi-resellers" accent="#B84A2B" />
        <Kpi icon={Package} label="Prodotti" value={d.products ?? 0} hint="attivi nel catalogo" testid="kpi-products" accent="#206342" />
        <Kpi icon={FileCheck2} label="Approvazioni pendenti" value={d.pending_docs ?? 0} hint="documenti e moduli" testid="kpi-approvals" accent="#C69214" />
        <Kpi icon={TrendingUp} label="Clienti attivi" value={d.customers ?? 0} hint="con licenza attiva" testid="kpi-customers" accent="#1E2A38" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Fatturato" value={formatEUR(d.revenue)} hint="licenze clienti attivi + rinnovi" testid="kpi-revenue" accent="#1E2A38" />
        <Kpi icon={TrendingUp} label="Provvigioni totali" value={formatEUR(d.commissions_total)} hint="maturate storico" testid="kpi-commissions-total" accent="#B84A2B" />
        <Kpi icon={CheckCircle2} label="Provvigioni liquidate" value={formatEUR(d.commissions_paid)} hint="pagate ai rivenditori" testid="kpi-commissions-paid" accent="#206342" />
        <Kpi icon={Clock} label="Provvigioni in attesa" value={formatEUR(d.commissions_pending)} hint="da liquidare" testid="kpi-commissions-pending" accent="#C69214" />
      </div>

      <Leaderboard api={api} />

      <RemindersCard api={api} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Link to="/admin/approvals" className="bg-white border border-[#E2DDD5] rounded-xl p-5 hover:shadow-md transition-all" data-testid="shortcut-approvals">
          <div className="font-semibold">Coda approvazioni →</div>
          <div className="text-sm text-slate-500 mt-1">Riveda documenti anagrafici, accordi firmati e moduli d'ordine clienti.</div>
        </Link>
        <Link to="/admin/products" className="bg-white border border-[#E2DDD5] rounded-xl p-5 hover:shadow-md transition-all" data-testid="shortcut-products">
          <div className="font-semibold">Catalogo prodotti →</div>
          <div className="text-sm text-slate-500 mt-1">Pubblichi un nuovo prodotto e assegni i moduli disponibili.</div>
        </Link>
        <Link to="/admin/resellers" className="bg-white border border-[#E2DDD5] rounded-xl p-5 hover:shadow-md transition-all" data-testid="shortcut-resellers">
          <div className="font-semibold">Rivenditori →</div>
          <div className="text-sm text-slate-500 mt-1">Gestisca anagrafiche, blocchi e piani provvigionali.</div>
        </Link>
      </div>
    </div>
  );
}
